import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AvailabilityDefault,
  AvailabilityEntry,
  RequestPeriod,
  ShiftPreferences,
  getDefaults,
  getMyRequest,
  getPreferences,
  listPeriods,
  saveDayRequest,
  saveDefaults,
  savePreferences,
  submitRequest,
} from '../services/shiftRequestService';

/**
 * 希望シフトの提出（従業員向け・スマートフォン想定）。
 *
 * ## 定型を敷いた状態で開く
 *
 * 曜日ごとの定型（「毎週 火・木 は 10:00–15:00」）を先に置き、
 * **違う日だけ直す**。固定の人はほぼ何も出さずに済む（設計 §5-5-3）。
 *
 * ## ⚠️ 締切を過ぎても「初回は」出せる
 *
 * 止めるより、後出しだと分かる方が運用に合う（代表判断）。画面にもそう出す。
 * ただし**出した後に書き換えられるかは別**（代表指摘 2026-09-02）。
 * 受付ごとの「期限内の再提出を許可する」と締切で決まる（`rewriteBlocked`）。
 */

const WEEK = ['日', '月', '火', '水', '木', '金', '土'];

const daysBetween = (from: string, to: string): string[] => {
  const out: string[] = [];
  const d = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (d <= end) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    d.setDate(d.getDate() + 1);
  }
  return out;
};

const hm = (t: string | null) => (t ? t.slice(0, 5) : '');

const MyShiftRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState<RequestPeriod[]>([]);
  const [periodId, setPeriodId] = useState<number | null>(null);
  const [period, setPeriod] = useState<RequestPeriod | null>(null);
  const [entries, setEntries] = useState<AvailabilityEntry[]>([]);
  const [defaults, setDefaults] = useState<AvailabilityDefault[]>([]);
  const [prefs, setPrefs] = useState<ShiftPreferences>({
    monthlyMinHours: null,
    monthlyMaxHours: null,
    weeklyMinHours: null,
    weeklyMaxHours: null,
  });
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [isLate, setIsLate] = useState(false);
  /*
   * 対象から外された受付。**未提出なら一覧にも出ない**(サーバが返さない)。
   * 店長など組む側の権限を持つ人も同じ — この画面は `mine` で引く。
   *
   * 提出済みの人は**そもそも対象から外せない**(サーバが 409 で断る)ので、
   * ここに来るのは 2026-09-02 より前に外された古いデータだけ。読むだけにする。
   */
  const [isTarget, setIsTarget] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planBlocked, setPlanBlocked] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ start: string; end: string }[]>([]);
  const [tab, setTab] = useState<'days' | 'defaults'>('days');

  useEffect(() => {
    (async () => {
      try {
        const ps = await listPeriods({ mine: true });
        setPeriods(ps);
        const open = ps.find((p) => p.status === 'open') ?? ps[0];
        setPeriodId(open?.id ?? null);
        if (!open) setLoading(false);
      } catch (e: any) {
        if (e.response?.data?.code === 'PLAN_REQUIRED') setPlanBlocked(true);
        else setError(e.response?.data?.error || '受付期間の取得に失敗しました');
        setLoading(false);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!periodId) return;
    try {
      setLoading(true);
      setError('');
      const [mine, prefRow] = await Promise.all([getMyRequest(periodId), getPreferences().catch(() => null)]);
      setPeriod(mine.period);
      setEntries(mine.entries);
      setDefaults(mine.defaults);
      setSubmittedAt(mine.submittedAt);
      setIsLate(mine.isLate);
      setIsTarget(mine.isTarget !== false);
      if (prefRow) setPrefs(prefRow);
    } catch (e: any) {
      if (e.response?.data?.code === 'PLAN_REQUIRED') setPlanBlocked(true);
      else setError(e.response?.data?.error || '提出内容の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * 出したものを**書き換えてよいか**（サーバの `rewriteBlock` と同じ判定）。
   *
   * まだ出していない人には効かない — 締切後の初回提出は通す。
   * ここで閉じるのは案内で、実際に止めているのはサーバ（403）。
   */
  const rewriteBlocked: string | null = useMemo(() => {
    if (!period || !submittedAt) return null;
    if (!period.allowResubmit) return 'この受付は、一度提出すると出し直しができません。';
    if (new Date(period.deadlineAt).getTime() < Date.now()) {
      return '締切を過ぎたため、提出した希望は直せません。';
    }
    return null;
  }, [period, submittedAt]);
  /** 編集・提出ができる状態か */
  const editable = isTarget && !rewriteBlocked;

  const days = useMemo(
    () => (period ? daysBetween(String(period.dateFrom).slice(0, 10), String(period.dateTo).slice(0, 10)) : []),
    [period]
  );

  /**
   * その日の予定。**提出があればそれ、無ければ定型**（定型を敷いた状態で見せる）。
   *
   * ⚠️ 提出のときに定型から起こした行（`fromDefault`）は、**画面では定型のまま**扱う。
   * そうしないと、提出した瞬間に全部の日が「変更あり」に見える（代表指摘 2026-09-02）。
   */
  const forDay = (date: string): { source: 'entry' | 'default' | 'none'; slots: AvailabilityEntry[] | AvailabilityDefault[] } => {
    const mine = entries.filter((e) => String(e.date).slice(0, 10) === date);
    if (mine.length > 0) return { source: mine.every((e) => e.fromDefault) ? 'default' : 'entry', slots: mine };
    const w = new Date(`${date}T00:00:00`).getDay();
    const def = defaults.filter((d) => d.weekday === w);
    if (def.length > 0) return { source: 'default', slots: def };
    return { source: 'none', slots: [] };
  };

  const openEditor = (date: string) => {
    if (!editable) return;
    const d = forDay(date);
    setEditing(date);
    setDraft(
      d.slots
        .filter((s: any) => s.kind === 'available')
        .map((s: any) => ({ start: hm(s.startTime), end: hm(s.endTime) }))
    );
  };

  const save = async (slots: { kind: 'available' | 'unavailable'; startTime?: string; endTime?: string }[]) => {
    if (!periodId || !editing) return;
    try {
      setError('');
      await saveDayRequest(periodId, editing, slots);
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.error || '保存に失敗しました');
    }
  };

  const doSubmit = async () => {
    if (!periodId) return;
    try {
      const r = await submitRequest(periodId);
      setSubmittedAt(r.submittedAt);
      setIsLate(r.isLate);
      window.alert(r.isLate ? '提出しました（締切後の提出です）' : '提出しました');
    } catch (e: any) {
      setError(e.response?.data?.error || '提出に失敗しました');
    }
  };

  if (planBlocked) {
    return (
      <div style={s.root}>
        <div style={s.card}>
          <h1 style={s.title}>希望シフト</h1>
          <p style={s.note}>この会社ではシフト管理をご利用いただけません。</p>
          <button onClick={() => navigate('/dashboard')} style={s.backBtn}>ダッシュボードに戻る</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.root}>
      <div style={s.card}>
        <h1 style={s.title}>希望シフトの提出</h1>

        {periods.length === 0 && !loading && (
          <p style={s.note}>いま受け付けている期間はありません。</p>
        )}

        {periods.length > 0 && (
          <select
            value={periodId ?? ''}
            onChange={(e) => setPeriodId(Number(e.target.value))}
            style={{ ...s.input, marginBottom: 12 }}
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.status === 'closed' ? '（締切済み）' : ''}
              </option>
            ))}
          </select>
        )}

        {error && <p style={s.error}>{error}</p>}

        {period && (
          <>
            <div style={s.periodBox}>
              <div>
                対象: {String(period.dateFrom).slice(0, 10)} 〜 {String(period.dateTo).slice(0, 10)}
              </div>
              <div>締切: {new Date(period.deadlineAt).toLocaleString('ja-JP')}</div>
              {submittedAt ? (
                <div style={{ marginTop: 6, color: isLate ? '#92400E' : '#166534', fontWeight: 700 }}>
                  提出済み {new Date(submittedAt).toLocaleString('ja-JP')}
                  {isLate && '（締切後）'}
                </div>
              ) : (
                <div style={{ marginTop: 6, color: '#991B1B', fontWeight: 700 }}>未提出</div>
              )}
            </div>

            {!isTarget && (
              <div style={s.notTarget}>
                この受付の<strong>対象から外れています</strong>。出した内容は残っていますが、
                この期間のシフトには使われません。直す必要があるときは担当者に伝えてください。
              </div>
            )}

            <div style={s.tabs}>
              <button onClick={() => setTab('days')} style={tab === 'days' ? s.tabOn : s.tabOff}>日ごとの希望</button>
              <button onClick={() => setTab('defaults')} style={tab === 'defaults' ? s.tabOn : s.tabOff}>いつもの予定</button>
            </div>

            {tab === 'defaults' ? (
              <DefaultsEditor defaults={defaults} prefs={prefs} onSaved={load} setPrefs={setPrefs} setError={setError} />
            ) : (
              <>
                <p style={{ ...s.note, marginBottom: 8 }}>
                  「いつもの予定」を敷いてあります。<strong>違う日だけ直してください。</strong>
                </p>
                <div style={{ display: 'grid', gap: 6 }}>
                  {days.map((date) => {
                    const d = forDay(date);
                    const w = new Date(`${date}T00:00:00`).getDay();
                    const unavailable = (d.slots as any[]).some((x) => x.kind === 'unavailable');
                    return (
                      <button
                        key={date}
                        onClick={() => openEditor(date)}
                        disabled={!editable}
                        style={{ ...s.dayRow, cursor: editable ? 'pointer' : 'default', opacity: editable ? 1 : 0.7 }}
                      >
                        <span style={{ minWidth: 56, fontWeight: 700 }}>
                          {Number(date.slice(8))}日
                          <span style={{ fontSize: 11, color: w === 0 ? '#DC2626' : w === 6 ? '#2563EB' : '#64748B' }}>
                            ({WEEK[w]})
                          </span>
                        </span>
                        <span style={{ flex: 1, textAlign: 'left', fontSize: 14 }}>
                          {d.source === 'none' ? (
                            <span style={{ color: '#CBD5E1' }}>未入力</span>
                          ) : unavailable ? (
                            <span style={{ color: '#991B1B' }}>出られない</span>
                          ) : (
                            (d.slots as any[]).map((x) => `${hm(x.startTime)}–${hm(x.endTime)}`).join('、')
                          )}
                        </span>
                        {/* 定型のままか、この日だけ直したかを区別する */}
                        <span style={{ fontSize: 11, color: '#94A3B8' }}>
                          {d.source === 'default' ? 'いつもの予定' : d.source === 'entry' ? '変更あり' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {editable && (
                  <button onClick={doSubmit} style={{ ...s.primaryBtn, width: '100%', marginTop: 18 }}>
                    {submittedAt ? 'この内容で出し直す' : 'この内容で提出する'}
                  </button>
                )}
                {/* 出し直せない理由は伏せない。「押せないボタン」より理由を出す */}
                {isTarget && rewriteBlocked && (
                  <p style={{ ...s.note, color: '#92400E' }}>{rewriteBlocked}</p>
                )}
                {editable && !submittedAt && period.status === 'open' && new Date(period.deadlineAt).getTime() < Date.now() && (
                  <p style={{ ...s.note, color: '#92400E' }}>締切を過ぎていますが、提出できます（後から出したことが分かります）。</p>
                )}
                {editable && submittedAt && (
                  <p style={s.note}>締切までは何度でも直せます。</p>
                )}
              </>
            )}
          </>
        )}

        <button onClick={() => navigate('/dashboard')} style={{ ...s.backBtn, marginTop: 20 }}>
          ダッシュボードに戻る
        </button>
      </div>

      {editing && (
        <div style={s.overlay} onClick={() => setEditing(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>{editing} の希望</h2>
            <div style={{ display: 'grid', gap: 8 }}>
              {draft.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="time"
                    value={r.start}
                    onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))}
                    style={s.input}
                  />
                  <span>–</span>
                  <input
                    type="time"
                    value={r.end}
                    onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))}
                    style={s.input}
                  />
                  <button onClick={() => setDraft(draft.filter((_, j) => j !== i))} style={s.smallBtn}>削除</button>
                </div>
              ))}
              {/* 1日に複数の時間帯を出せる。「10:00–15:00、18:00–22:00」のような希望がある */}
              {draft.length < 4 && (
                <button onClick={() => setDraft([...draft, { start: '10:00', end: '18:00' }])} style={s.smallBtn}>
                  ＋ 時間帯を足す
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
              <button
                onClick={() =>
                  save(draft.filter((r) => r.start && r.end).map((r) => ({ kind: 'available', startTime: r.start, endTime: r.end })))
                }
                style={s.primaryBtn}
              >
                この時間で出す
              </button>
              <button onClick={() => save([{ kind: 'unavailable' }])} style={s.backBtn}>
                この日は出られない
              </button>
              {/* 空で保存すると定型が復活する */}
              <button onClick={() => save([])} style={s.linkBtn}>いつもの予定に戻す</button>
              <button onClick={() => setEditing(null)} style={s.linkBtn}>閉じる</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** 定型（曜日ごと）と、本人の条件 */
const DefaultsEditor: React.FC<{
  defaults: AvailabilityDefault[];
  prefs: ShiftPreferences;
  setPrefs: (p: ShiftPreferences) => void;
  onSaved: () => void;
  setError: (e: string) => void;
}> = ({ defaults, prefs, setPrefs, onSaved, setError }) => {
  const [weekday, setWeekday] = useState(1);
  const [rows, setRows] = useState<{ start: string; end: string }[]>([]);

  useEffect(() => {
    setRows(
      defaults.filter((d) => d.weekday === weekday && d.kind === 'available').map((d) => ({ start: hm(d.startTime), end: hm(d.endTime) }))
    );
  }, [weekday, defaults]);

  const persist = async (slots: any[]) => {
    try {
      setError('');
      await saveDefaults(weekday, slots);
      onSaved();
    } catch (e: any) {
      setError(e.response?.data?.error || '保存に失敗しました');
    }
  };

  return (
    <div>
      <p style={{ ...s.note, marginBottom: 10 }}>
        毎週の決まった予定を入れておくと、<strong>提出のときに毎回入力しなくて済みます。</strong>
      </p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {WEEK.map((w, i) => (
          <button key={w} onClick={() => setWeekday(i)} style={weekday === i ? s.tabOn : s.tabOff}>
            {w}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="time" value={r.start} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} style={s.input} />
            <span>–</span>
            <input type="time" value={r.end} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} style={s.input} />
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} style={s.smallBtn}>削除</button>
          </div>
        ))}
        {rows.length < 4 && (
          <button onClick={() => setRows([...rows, { start: '10:00', end: '18:00' }])} style={s.smallBtn}>
            ＋ 時間帯を足す
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={() => persist(rows.filter((r) => r.start && r.end).map((r) => ({ kind: 'available', startTime: r.start, endTime: r.end })))}
          style={s.primaryBtn}
        >
          {WEEK[weekday]}曜日の予定を保存
        </button>
        <button onClick={() => persist([])} style={s.backBtn}>この曜日は空にする</button>
      </div>

      <h3 style={{ fontSize: 15, margin: '24px 0 8px' }}>働きたい時間</h3>
      <p style={{ ...s.note, marginBottom: 10 }}>
        入れておくと、シフトを組むときの目安になります。空のままでも構いません。
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
        {([
          ['monthlyMinHours', '月に最低'],
          ['monthlyMaxHours', '月に最大'],
          ['weeklyMinHours', '週に最低'],
          ['weeklyMaxHours', '週に最大'],
        ] as const).map(([key, label]) => (
          <label key={key} style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
            <span style={{ marginBottom: 4 }}>{label}</span>
            <input
              type="number"
              min={0}
              value={prefs[key] ?? ''}
              onChange={(e) => setPrefs({ ...prefs, [key]: e.target.value === '' ? null : Number(e.target.value) })}
              style={s.input}
              placeholder="時間"
            />
          </label>
        ))}
      </div>
      <button
        onClick={async () => {
          try {
            setError('');
            await savePreferences(prefs);
            onSaved();
          } catch (e: any) {
            setError(e.response?.data?.error || '保存に失敗しました');
          }
        }}
        style={{ ...s.primaryBtn, marginTop: 12 }}
      >
        働きたい時間を保存
      </button>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh', background: '#F1F5F9', padding: 16 },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #dee2e6', padding: 'clamp(16px, 4vw, 28px)', width: 'min(96vw, 640px)', margin: '0 auto' },
  title: { margin: '0 0 16px', fontSize: 'clamp(17px, 4vw, 21px)' },
  note: { color: '#6c757d', fontSize: 13, lineHeight: 1.8, margin: 0 },
  error: { color: '#721c24', background: '#f8d7da', borderRadius: 4, padding: 10, fontSize: 14, marginTop: 12 },
  input: { padding: '8px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14, width: '100%', boxSizing: 'border-box' },
  periodBox: { background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: 12, fontSize: 13, lineHeight: 1.8 },
  notTarget: { background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E', borderRadius: 8, padding: 12, fontSize: 13, lineHeight: 1.7, marginTop: 10 },
  tabs: { display: 'flex', gap: 6, margin: '14px 0' },
  tabOn: { background: '#2563EB', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  tabOff: { background: '#fff', color: '#475569', border: '1px solid #CBD5E1', borderRadius: 6, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  dayRow: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', cursor: 'pointer', textAlign: 'left' },
  primaryBtn: { background: '#2563EB', color: '#fff', border: 'none', borderRadius: 6, padding: '11px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  backBtn: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 18px', fontSize: 13, cursor: 'pointer' },
  smallBtn: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  linkBtn: { background: 'none', border: 'none', color: '#0969da', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 4 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 },
  modal: { background: '#fff', borderRadius: 12, padding: 20, width: 'min(94vw, 420px)' },
};

export default MyShiftRequestPage;
